import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import SectionLabel from '../ui/SectionLabel';
import './ScreenshotsSection.css';
import cowimage from '../../images/cowimage.png';
import logoMark from '../../images/logo-mark.png';
import farmerimage from '../../images/farmerimage.png';

/* ============================================================
   Screen content — matches the real Dhenika app screens
   (Home, My Animals, Animal Profile, Reminders) instead of
   placeholder copy.
   ============================================================ */

const screenshots = [
  {
    id: 1,
    label: 'Home',
    description: 'Your full farm overview, reminders, and quick actions in one place.',
    screen: [
      { type: 'appbar', title: 'Dhenika', sub: "Shinde's Farm" },
      {
        type: 'stat-grid',
        items: [
          { icon: '🐄', value: 3, label: 'Total' },
          { icon: '🤰', value: 0, label: 'Pregnant' },
          { icon: '🏥', value: 1, label: 'Sick' },
          { icon: '✅', value: 2, label: 'Healthy' },
        ],
      },
      { type: 'section-label', text: "Today's Reminders", action: 'See all' },
      { type: 'reminder-banner', name: 'Guari', task: '🌡️ Heat Check', pill: 'Overdue' },
      { type: 'section-label', text: 'Quick Tasks' },
      {
        type: 'quick-grid',
        items: [
          { icon: '💉', title: 'Vaccination', desc: 'Select cow → log vaccine', tone: 'blue' },
          { icon: '🩺', title: 'Medical record', desc: 'Select cow → log illness', tone: 'green' },
          { icon: '🐄', title: 'Add cow', desc: 'Register a new animal', tone: 'yellow' },
          { icon: '🐛', title: 'Deworming', desc: 'Select cow → schedule', tone: 'purple' },
        ],
      },
      { type: 'wide-task', icon: '🧬', title: 'Add breeding', desc: 'Log a breeding event', tone: 'pink' },
    ],
  },
  {
    id: 2,
    label: 'My Animals',
    description: 'Search, filter, and check every animal on your farm in seconds.',
    screen: [
      { type: 'list-header', kicker: 'YOUR FARM', title: 'My Animals', action: '+ Add Animal' },
      {
        type: 'stat-grid',
        items: [
          { icon: '🐄', value: 3, label: 'Total' },
          { icon: '✅', value: 2, label: 'Normal' },
          { icon: '🤰', value: 0, label: 'Pregnant' },
          { icon: '🏥', value: 1, label: 'Sick' },
        ],
      },
      { type: 'search', placeholder: 'Search by name, tag, or breed…' },
      { type: 'chips', options: ['All', 'Normal', 'Pregnant', 'Sick'], active: 0 },
      {
        type: 'animal-list',
        items: [
          { name: 'Gauri', tag: 'AM4545', breed: 'Jersey · 11 yrs old', status: 'Sick' },
          { name: 'gunga', tag: 'AM3433', breed: 'Tharparkar · 6 yrs old', status: 'Normal' },
          { name: 'Sarvadnya', tag: 'AM1111', breed: 'Gir · 2 yrs old', status: 'Normal' },
        ],
      },
    ],
  },
  {
    id: 3,
    label: 'Animal Profile',
    description: 'A full history for every animal — weight, age, vaccinations, and more.',
    screen: [
      { type: 'back-header', kicker: 'ANIMAL PROFILE', title: 'Guari' },
      { type: 'profile-card', name: 'Guari', status: 'Sick', breed: 'Gir · cow', tag: 'AM4545' },
      {
        type: 'profile-metrics',
        items: [
          { value: '123 kg', label: 'Weight' },
          { value: '4 yrs', label: 'Age' },
          { value: '4 Jul 2022', label: 'Date of Birth' },
        ],
      },
      { type: 'profile-tabs', options: ['Overview', 'Vaccination', 'Deworming', 'Medical'], active: 0 },
      { type: 'profile-photo', tag: 'adult' },
      { type: 'more-details' },
    ],
  },
  {
    id: 4,
    label: 'Reminders',
    description: 'Never miss a vaccine, deworming, or heat check again.',
    screen: [
      { type: 'page-header', title: 'Reminders', sub: '1 need attention', badge: 1 },
      {
        type: 'reminder-stats',
        items: [
          { value: 1, label: 'Overdue', tone: 'red' },
          { value: 0, label: 'Today', tone: 'amber' },
          { value: 0, label: 'Upcoming', tone: 'green' },
        ],
      },
      { type: 'section-label', text: 'STATUS' },
      { type: 'chips', options: ['All', 'Overdue', 'Today', 'Upcoming'], active: 0 },
      { type: 'section-label', text: 'TYPE' },
      { type: 'chips', options: ['All', '💉 Vaccine', '🌿 Deworm', '🧬 Breeding'], active: 0 },
      { type: 'tip', text: '👆 Tap any reminder for details and actions' },
      { type: 'section-label', text: 'OVERDUE', action: '1' },
      {
        type: 'reminder-detail',
        icon: '🌡️',
        name: 'Guari',
        tag: 'AM3433',
        status: 'Overdue',
        typeLabel: 'Heat Check',
        note: 'Watch for heat signs (restlessness, mounting)…',
        dates: '23 Jul – 25 Jul 2026',
      },
    ],
  },
];

/* ============================================================
   Small hooks
   ============================================================ */

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);

    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);

  return { ref, visible, reducedMotion };
}

function useCountUp(values, ready, reducedMotion) {
  const [counts, setCounts] = useState(values.map(() => 0));

  useEffect(() => {
    if (!ready) return undefined;
    if (reducedMotion) {
      setCounts(values);
      return undefined;
    }
    const timers = values.map((target, i) =>
      setTimeout(() => {
        const duration = 500;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCounts((prev) => {
            const next = [...prev];
            next[i] = Math.round(eased * target);
            return next;
          });
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, i * 90)
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, reducedMotion, values.join(',')]);

  return counts;
}

/* ============================================================
   Phone screen renderer
   ============================================================ */

function StatGrid({ items, screenKey }) {
  const counts = useCountUp(items.map((i) => i.value), true, false);
  return (
    <div className="ss__screen-statgrid">
      {items.map((s, i) => (
        <div className="ss__screen-statbox" key={`${screenKey}-${i}`}>
          <span className="ss__screen-statbox-value">{counts[i]}</span>
          <span className="ss__screen-statbox-icon">{s.icon}</span>
          <span className="ss__screen-statbox-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function PhoneScreen({ screen, screenKey }) {
  return (
    <div className="ss__phone-screen" key={screenKey}>
      {screen.map((item, i) => {
        const style = { '--ss-delay': `${i * 45}ms` };

        switch (item.type) {
          case 'appbar':
            return (
              <div key={i} className="ss__screen-appbar" style={style}>
                <div className="ss__screen-appbar-brand">
                  <span className="ss__screen-appbar-mark">
                    <img src={logoMark} alt="" className="ss__screen-appbar-mark-img" />
                  </span>
                  <div>
                    <div className="ss__screen-appbar-title">{item.title}</div>
                    <div className="ss__screen-appbar-sub">{item.sub}</div>
                  </div>
                </div>
                <img src={farmerimage} alt="" className="ss__screen-appbar-avatar" />
              </div>
            );

          case 'list-header':
            return (
              <div key={i} className="ss__screen-listheader" style={style}>
                <div>
                  <div className="ss__screen-kicker">{item.kicker}</div>
                  <div className="ss__screen-listheader-title">{item.title}</div>
                </div>
                <span className="ss__screen-pillbtn">{item.action}</span>
              </div>
            );

          case 'back-header':
            return (
              <div key={i} className="ss__screen-listheader" style={style}>
                <div>
                  <div className="ss__screen-kicker">{item.kicker}</div>
                  <div className="ss__screen-listheader-title">{item.title}</div>
                </div>
              </div>
            );

          case 'page-header':
            return (
              <div key={i} className="ss__screen-listheader" style={style}>
                <div>
                  <div className="ss__screen-listheader-title">{item.title}</div>
                  <div className="ss__screen-appbar-sub">{item.sub}</div>
                </div>
                <span className="ss__screen-badgecount">{item.badge}</span>
              </div>
            );

          case 'stat-grid':
            return <div key={i} style={style}><StatGrid items={item.items} screenKey={`${screenKey}-${i}`} /></div>;

          case 'reminder-stats':
            return (
              <div key={i} className="ss__screen-statgrid ss__screen-statgrid--3" style={style}>
                {item.items.map((s, j) => (
                  <div className={`ss__screen-statbox ss__screen-statbox--${s.tone}`} key={j}>
                    <span className="ss__screen-statbox-value">{s.value}</span>
                    <span className="ss__screen-statbox-label">{s.label}</span>
                  </div>
                ))}
              </div>
            );

          case 'section-label':
            return (
              <div key={i} className="ss__screen-sectionlabel" style={style}>
                <span>{item.text}</span>
                {item.action && <span className="ss__screen-link">{item.action}</span>}
              </div>
            );

          case 'reminder-banner':
            return (
              <div key={i} className="ss__screen-reminder ss__screen-reminder--urgent" style={style}>
                <span className="ss__screen-reminder-dot" />
                <div className="ss__screen-record-info">
                  <span className="ss__screen-record-title">{item.name}</span>
                  <span className="ss__screen-record-date">{item.task}</span>
                </div>
                <span className="ss__screen-badge ss__screen-badge--red">{item.pill}</span>
              </div>
            );

          case 'quick-grid':
            return (
              <div key={i} className="ss__screen-quickgrid" style={style}>
                {item.items.map((t, j) => (
                  <div className={`ss__screen-quicktile ss__screen-quicktile--${t.tone}`} key={j}>
                    <span className="ss__screen-quicktile-icon">{t.icon}</span>
                    <span className="ss__screen-quicktile-title">{t.title}</span>
                    <span className="ss__screen-quicktile-desc">{t.desc}</span>
                  </div>
                ))}
              </div>
            );

          case 'wide-task':
            return (
              <div key={i} className={`ss__screen-quicktile ss__screen-quicktile--${item.tone} ss__screen-quicktile--wide`} style={style}>
                <span className="ss__screen-quicktile-icon">{item.icon}</span>
                <div>
                  <span className="ss__screen-quicktile-title">{item.title}</span>
                  <span className="ss__screen-quicktile-desc">{item.desc}</span>
                </div>
              </div>
            );

          case 'search':
            return (
              <div key={i} className="ss__screen-search" style={style}>
                🔍 <span>{item.placeholder}</span>
              </div>
            );

          case 'chips':
            return (
              <div key={i} className="ss__screen-chips" style={style}>
                {item.options.map((opt, j) => (
                  <span key={j} className={`ss__screen-chip ${j === item.active ? 'ss__screen-chip--active' : ''}`}>
                    {opt}
                  </span>
                ))}
              </div>
            );

          case 'animal-list':
            return (
              <div key={i} className="ss__screen-animallist" style={style}>
                {item.items.map((a, j) => (
                  <div className="ss__screen-card" key={j} style={{ '--ss-delay': `${(i + j) * 55}ms` }}>
                    <span className="ss__screen-card-icon">🐄</span>
                    <div className="ss__screen-card-info">
                      <span className="ss__screen-card-title">
                        {a.name} <span className="ss__screen-tag">{a.tag}</span>
                      </span>
                      <span className="ss__screen-card-sub">{a.breed}</span>
                    </div>
                    <span className={`ss__screen-badge ${a.status === 'Sick' ? 'ss__screen-badge--red' : 'ss__screen-badge--green'}`}>
                      {a.status}
                    </span>
                  </div>
                ))}
              </div>
            );

          case 'profile-card':
            return (
              <div key={i} className="ss__screen-profilecard" style={style}>
                <img
                  src={cowimage}
                  alt={item.name}
                  className="ss__screen-profilecard-photo"
                />
                <div className="ss__screen-profilecard-info">
                  <span className="ss__screen-profilecard-name">{item.name}</span>
                  <span className="ss__screen-badge ss__screen-badge--red ss__screen-badge--inline">{item.status}</span>
                  <span className="ss__screen-card-sub">{item.breed}</span>
                  <span className="ss__screen-tag ss__screen-tag--standalone">🏷️ {item.tag}</span>
                </div>
              </div>
            );

          case 'profile-metrics':
            return (
              <div key={i} className="ss__screen-metrics" style={style}>
                {item.items.map((m, j) => (
                  <div className="ss__screen-metric" key={j}>
                    <span className="ss__screen-metric-value">{m.value}</span>
                    <span className="ss__screen-metric-label">{m.label}</span>
                  </div>
                ))}
              </div>
            );

          case 'profile-tabs':
            return (
              <div key={i} className="ss__screen-chips" style={style}>
                {item.options.map((opt, j) => (
                  <span key={j} className={`ss__screen-chip ${j === item.active ? 'ss__screen-chip--active' : ''}`}>
                    {opt}
                  </span>
                ))}
              </div>
            );

          case 'profile-photo':
            return (
              <div key={i} className="ss__screen-photo" style={style}>
                <img
                  src={cowimage}
                  alt="Animal"
                  className="ss__screen-photo-img"
                />
                <span className="ss__screen-tag ss__screen-tag--onphoto">🐾 {item.tag}</span>
              </div>
            );

          case 'more-details':
            return (
              <div key={i} className="ss__screen-record" style={style}>
                <span className="ss__screen-record-icon">🪪</span>
                <span className="ss__screen-record-title">More details</span>
              </div>
            );

          case 'tip':
            return (
              <div key={i} className="ss__screen-tip" style={style}>
                {item.text}
              </div>
            );

          case 'reminder-detail':
            return (
              <div key={i} className="ss__screen-reminderdetail" style={style}>
                <div className="ss__screen-reminderdetail-top">
                  <span className="ss__screen-record-icon">{item.icon}</span>
                  <div className="ss__screen-record-info">
                    <span className="ss__screen-record-title">
                      {item.name} <span className="ss__screen-tag">{item.tag}</span>
                    </span>
                  </div>
                  <div className="ss__screen-reminderdetail-badges">
                    <span className="ss__screen-badge ss__screen-badge--red">{item.status}</span>
                    <span className="ss__screen-badge ss__screen-badge--amber">{item.typeLabel}</span>
                  </div>
                </div>
                <p className="ss__screen-reminderdetail-note">{item.note}</p>
                <span className="ss__screen-reminderdetail-dates">{item.dates}</span>
              </div>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}

/* ============================================================
   Main section
   ============================================================ */

// Minimum horizontal drag (px) before a swipe counts as a tab change.
const SWIPE_THRESHOLD = 45;
// If the finger moves more vertically than this ratio relative to
// horizontal movement, treat it as a page-scroll, not a swipe.
const SWIPE_DIRECTION_RATIO = 1.2;

function ScreenshotsSection() {
  const [active, setActive] = useState(0);
  const current = screenshots[active];
  const { ref, visible, reducedMotion } = useReveal();

  const tabRefs = useRef([]);
  const tabsWrapRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  // ── Swipe state (mobile) ──
  const touchStart = useRef({ x: 0, y: 0 });
  const isSwiping = useRef(false);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const goTo = (index) => {
    const clamped = Math.max(0, Math.min(screenshots.length - 1, index));
    setActive(clamped);
  };

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
    isSwiping.current = true;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isSwiping.current) return;
    const t = e.touches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;

    // If the gesture is clearly more vertical than horizontal, let the
    // page scroll normally instead of hijacking it as a swipe.
    if (Math.abs(dy) > Math.abs(dx) * SWIPE_DIRECTION_RATIO) {
      isSwiping.current = false;
      setIsDragging(false);
      setDragX(0);
      return;
    }

    // Only fight the browser's native scroll once horizontal intent
    // is clear, and don't allow dragging past the first/last screen.
    if (Math.abs(dx) > 8) {
      e.preventDefault();
      const atStart = active === 0 && dx > 0;
      const atEnd = active === screenshots.length - 1 && dx < 0;
      setDragX(atStart || atEnd ? dx * 0.35 : dx);
    }
  };

  const handleTouchEnd = () => {
    if (!isSwiping.current) {
      isSwiping.current = false;
      return;
    }
    isSwiping.current = false;
    setIsDragging(false);

    if (dragX <= -SWIPE_THRESHOLD) {
      goTo(active + 1);
    } else if (dragX >= SWIPE_THRESHOLD) {
      goTo(active - 1);
    }
    setDragX(0);
  };

  useLayoutEffect(() => {
    const node = tabRefs.current[active];
    const wrap = tabsWrapRef.current;
    if (!node || !wrap) return;
    const nodeRect = node.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    setIndicator({
      left: nodeRect.left - wrapRect.left + wrap.scrollLeft,
      width: nodeRect.width,
    });

    // Keep the active tab inside the visible strip — matters most when
    // `active` changes from a swipe rather than a direct tab click,
    // since the newly-active tab may currently be scrolled off-screen.
    node.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }, [active, reducedMotion]);

  useEffect(() => {
    const onResize = () => {
      const node = tabRefs.current[active];
      const wrap = tabsWrapRef.current;
      if (!node || !wrap) return;
      const nodeRect = node.getBoundingClientRect();
      const wrapRect = wrap.getBoundingClientRect();
      setIndicator({
        left: nodeRect.left - wrapRect.left + wrap.scrollLeft,
        width: nodeRect.width,
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <section className={`ss${visible ? ' is-visible' : ''}`} id="screenshots" ref={ref}>
      <div className="ss__container">

        {/* ── Header ── */}
        <div className="ss__header">
          <SectionLabel text="📱 App Screenshots" />
          <h2 className="ss__heading">
            See Dhenika{' '}
            <span className="ss__heading--accent">in action.</span>
          </h2>
          <p className="ss__subtext">
            Clean, simple screens designed for every farmer — no tech knowledge needed.
          </p>
        </div>

        {/* ── Tabs (still work as a fallback / on desktop) ── */}
        <div className="ss__tabs" ref={tabsWrapRef}>
          <span
            className="ss__tab-indicator"
            style={{
              transform: `translateX(${indicator.left}px)`,
              width: indicator.width,
              transitionDuration: reducedMotion ? '0s' : undefined,
            }}
            aria-hidden="true"
          />
          {screenshots.map((s, i) => (
            <button
              key={s.id}
              ref={(el) => (tabRefs.current[i] = el)}
              className={`ss__tab ${i === active ? 'ss__tab--active' : ''}`}
              onClick={() => goTo(i)}
              type="button"
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* ── Content (swipeable on touch devices) ── */}
        <div
          className={`ss__content${isDragging ? ' ss__content--dragging' : ''}`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          {/* Phone */}
          <div className="ss__phone-wrap">
            <div className="ss__phone-glow" aria-hidden="true" />
            <div
              className="ss__phone"
              style={{
                transform: dragX ? `translateX(${dragX}px)` : undefined,
                transition: isDragging ? 'none' : undefined,
              }}
            >
              <div className="ss__phone-notch" />
              <PhoneScreen screen={current.screen} screenKey={current.id} />
            </div>
          </div>

          {/* Description */}
          <div className="ss__info" key={current.id}>
            <h3 className="ss__info-title">{current.label}</h3>
            <p className="ss__info-desc">{current.description}</p>
            <div className="ss__info-dots">
              {screenshots.map((_, i) => (
                <button
                  key={i}
                  className={`ss__dot ${i === active ? 'ss__dot--active' : ''}`}
                  onClick={() => goTo(i)}
                  aria-label={`View ${screenshots[i].label}`}
                  type="button"
                />
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

export default ScreenshotsSection;